import React from "react";

import { formatPhoneNumber } from "../../../Utils/helper";
import avatarImage from "../../../assets/images/avatar.svg";

function InsuranceSummaryTable({ insurances = [] }) {
  const tableRowStyle = {
    height: "42px",
    fontWeight: "600",
    // lineHeight: "42px",
  };

  const emptyRowStyle = {
    height: "42px",
    width: "100%",
  };

  const formatWithCommas = (value) => {
    if (!value || isNaN(Number(value))) {
      return "-";
    }
    return Number(value).toLocaleString();
  };

  const getName = (type, data = {}) => {
    if (!data) return "";
    const { first_name = "", last_name = "", entity_name = "", defendantType = "" } = data;

    const fullName = `${first_name ?? ""} ${last_name ?? ""}`?.trim();

    if (type === "Defendant") {
      return defendantType?.id === 1 ? fullName : entity_name || "-";
    }

    if (type === "Client") {
      return fullName;
    }

    if (type === "Witness") {
      return `${data?.witness_first_name ?? ""} ${data?.witness_last_name ?? ""}`?.trim();
    }

    if (type === "OtherParty") {
      const fullName2 = `${data?.party_first_name ?? ""} ${data?.party_last_name ?? ""}`?.trim();
      return fullName2;
    }

    return "-";
  };

  const getPartyIcon = (partyType) => {
    switch (partyType?.toLowerCase()) {
      case "client":
        return "ic ic-15 ic-client";
      case "defendant":
        return "ic ic-15 ic-defendants";
      case "otherparty":
        return "ic ic-15 ic-parties";
      case "witness":
        return "ic ic-15 ic-witnesses";
      default:
        return "ic ic-15 ic-client";
    }
  };

  const renderDataRow = (insurance, index) => (
    <tr key={`insurance-${index}`} style={tableRowStyle}>
      {/* Index */}
      <td
        className="text-center"
        style={{
          minWidth: "25px",
          width: "25px",
          maxWidth: "25px",
          color: "var(--primary-25)",
          fontWeight: "600",
        }}
      >
        {index + 1}
      </td>

      {/* Party */}
      <td className="td-autosize p-l-5">
        <div className="d-flex align-items-center" style={{ gap: "5px" }}>
          {insurance?.entity_type?.toLowerCase() === "client" ? (
            <span className="ic-avatar ic-15 has-avatar-icon has-cover-img">
              <img
                className="output-18 theme-ring"
                src={insurance?.entity_obj?.profile_pic_19p || avatarImage}
                alt={getName(insurance?.entity_type, insurance?.entity_obj) || "Client"}
                onError={(event) => {
                  event.currentTarget.src = avatarImage;
                }}
                style={{
                  background: "var(--primary-30)",
                }}
              />
            </span>
          ) : (
            <i className={getPartyIcon(insurance?.entity_type)} />
          )}
          <span className="text-truncate">
            {getName(insurance?.entity_type, insurance?.entity_obj) || "-"}
          </span>
        </div>
      </td>

      <td />

      {/* Insurance */}
      <td className="text-end" style={{ width: "1%" }}>
        <div className="d-flex flex-column align-items-end text-end">
          <span className="text-truncate">{insurance?.insurance_type?.name || "-"}</span>
          <span className="text-truncate">{insurance?.company_contact?.name || "-"}</span>
        </div>
      </td>

      <td />

      {/* Limits */}
      <td className="text-center">
        <div>
          {insurance?.liabilityLimit || insurance?.liabilityLimitAll
            ? `$ ${formatWithCommas(insurance?.liabilityLimit)} / ${formatWithCommas(insurance?.liabilityLimitAll)}`
            : "-"}
        </div>
      </td>

      <td />

      {/* Policy */}
      <td className="text-center">
        <div>{insurance?.policy_number || "-"}</div>
      </td>

      <td />

      {/* Claim */}
      <td className="text-center">
        <div>{insurance?.claim_number || "-"}</div>
      </td>

      <td />

      {/* Adjuster */}
      <td>
        <div className="d-flex flex-column">
          <span className="text-truncate">
            {insurance?.adjuster?.first_name || insurance?.adjuster?.last_name
              ? `${insurance.adjuster.first_name || ""} ${insurance.adjuster.last_name || ""}`.trim()
              : "-"}
          </span>
          {insurance?.adjuster?.phone_number && (
            <span className="text-truncate">
              {formatPhoneNumber(insurance.adjuster.phone_number)}
            </span>
          )}
          {/* {insurance?.adjuster?.fax && (
            <span className="text-truncate">Fax: {insurance.adjuster.fax}</span>
          )}
          {insurance?.adjuster?.email && (
            <span className="text-truncate">{insurance.adjuster.email}</span>
          )} */}
        </div>
      </td>

      <td />
    </tr>
  );

  const renderEmptyRows = (dataLength) => {
    const minRows = 5;
    const emptyRowsCount = Math.max(0, minRows - dataLength);

    return Array.from({ length: emptyRowsCount }, (_, index) => (
      <tr key={`empty-${index}`} style={tableRowStyle}>
        <td colSpan={13} className="empty-row">
          <div style={emptyRowStyle}>{/* Empty row */}</div>
        </td>
      </tr>
    ));
  };

  return (
    <div className="custom-tab lit-custom-tab w-100">
      <div className="table-responsive overflow-hidden">
        <table className="table table-borderless table-striped table-treatment has-height-25">
          <thead>
            <tr id="tb-header">
              <th
                className="text-center"
                style={{
                  minWidth: "25px",
                  width: "25px",
                  maxWidth: "25px",
                }}
              >
                {/* Index */}
              </th>
              <th className="td-autosize text-center color-grey-2 p-l-5">Party</th>
              <th />
              <th className="text-center color-grey-2" style={{ width: "1%" }}>
                Insurance
              </th>
              <th />
              <th className="text-center color-grey-2 td-autosize">Limits</th>
              <th />
              <th className="text-center color-grey-2 td-autosize">Policy</th>
              <th />
              <th className="text-center color-grey-2 td-autosize">Claim</th>
              <th />
              <th className="text-center color-grey-2 td-autosize">Adjuster</th>
              <th
                style={{
                  minWidth: "10px",
                  width: "10px",
                  maxWidth: "10px",
                }}
              />
            </tr>
          </thead>
          <tbody>
            {insurances?.map((insurance, index) => renderDataRow(insurance, index))}
            {renderEmptyRows(insurances?.length || 0)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default React.memo(InsuranceSummaryTable);
